// frontend/src/components/homepage/HomeNav.js
//
// The homepage's floating pill navbar - a literal port of the prototype's
// <header class="nav"> (docs/homepage-concepts/concept-01b-live-mesh-light.html)
// and of the "NAV: compact on scroll + scroll-spy" block of its script, plus
// the magnet on the primary CTA. Styles: src/homepage/homeNav.css.
//
// It serves EVERY PUBLIC route (owner round 2): AuthLayout renders it in
// place of the production Navbar when `navVariant="home"`, which is the
// default whenever `isPublic` is true - the homepage, the blog index and
// articles, the three landings and the legal pages for a signed-out visitor.
// Authenticated routes keep the app chrome untouched. Off the homepage the
// section links become RouterLinks to `/#<id>` (Homepage.js's useScrollToHash
// scrolls on arrival), the scroll-spy is inert, the dock slot stays empty
// (ScannedTray only exists on the homepage), and a spacer the height of the
// pill plus its top offset is rendered after the fixed header so the page's
// own first block starts UNDER the pill, as the production Navbar's Toolbar
// spacer did.
//
// FOUR BEHAVIOURS, none of them a loop:
//   * compact  - `scrollY > 80` toggles `hl-nav-compact` (the pill narrows to
//                1120px). A passive scroll listener that writes state only
//                when the answer changes.
//   * has-dock - once the hero's scanned-posts counter docks into the bar the
//                pill goes back to 1380px and solid white. ScannedTray.js owns
//                the dock: it sizes the `#lf-nav-dock` slot's inline width and
//                never touches this component, so the nav LISTENS to the slot
//                (a MutationObserver on its style attribute) instead of being
//                told. Zero width = undocked.
//   * scroll-spy - the prototype's IntersectionObserver, rootMargin
//                `-40% 0px -55% 0px`: a link is `.active` (and carries
//                `aria-current="location"`) while its section crosses that
//                band. Nothing is marked until a spied section reaches it, and
//                the hero is observed too, as a CLEARING target: scrolling
//                back up into it un-lights the last section (final review
//                #14) - so "לקוחות" is never lit while the hero is on screen.
//   * magnet   - the primary CTA leans toward the pointer (dx*.22, dy*.28).
//                Pointer-driven only, no rAF, and NOT attached at all under
//                motion-off or on a `(hover: none)` device, exactly the
//                prototype's `TOUCH || REDUCED` bail.
//
// TWO ADDITIONS the prototype does not have (owner round 2):
//   * utilities - the language switch and the accessibility settings, which
//                the production Navbar exposes on every other route, sit as a
//                quiet hairline-separated cluster at the trailing edge of the
//                actions (between the section links and the CTAs). Same 999
//                radius family, a teal wash on hover.
//   * phone menu - under 1080px (where the prototype simply hides the links)
//                the shared round burger (44px; a 38px circle with a 44px hit
//                area at <= 600px) at the pill's RIGHT end opens a glass
//                sheet anchored to the pill's right edge: the seven anchors
//                each with its lucide icon, the utilities row and the two
//                CTAs full-width. It closes on a link tap, Escape, an outside
//                tap and on growing past the breakpoint; body scroll is
//                locked and focus trapped (MUI's FocusTrap, the same one
//                Dialog uses) while it is open, and the page behind it - the
//                pill's own wordmark / links / actions and AuthLayout's
//                <main> - is `inert` (final review #7). The burger stays
//                live: it is the sheet's toggle. It is UNMOUNTED when closed.
//                Focus returns to the burger only on a close the visitor made
//                (tap, Escape, link, scrim) - not on the media-query close,
//                which happens under nobody's hands.
//
// THE PHONE PILL IS WORDMARK-LEFT, BURGER-RIGHT AND NOTHING ELSE AT REST
// (owner round 3, 2026-09-04). The burger is the LAST child of the pill's row
// - not a CSS `order` flip - so the tab order matches what the eye sees; on a
// wide screen it is `display:none` and the row is exactly the prototype's. The
// login link and the utilities live in the sheet there, and the primary CTA is
// collapsed to zero width until the visitor has scrolled PAST the hero's own
// register button, at which point it slides in immediately beside the burger.
// The sentinel is the hero CTA itself (`[data-hero-cta]`, HeroLive.js) watched
// by an IntersectionObserver - not a scroll threshold: the button sits at 401px
// on a 390 phone and at 595px on a laptop, so any fixed number would be wrong
// on one of them. Off the homepage (and if the hero never appears) the CTA is
// simply shown, which is the pre-round-3 behaviour.
//
// DIRECTION: the pill's inner row is physically LTR in both languages (the
// wordmark stays at the left, the actions at the right - the prototype's
// layout), but the links row and the actions cluster take the page language
// as a `dir` attribute so English reads Clients ... Blog left to right and the
// utilities' separator sits on the correct side (final review #1).
//
// Anchors are plain `<a href="#id">`: the browser's own fragment navigation
// scrolls (index.css puts `scroll-behavior: smooth` on <html>, as the
// prototype does), and Homepage.js's useScrollToHash follows the router hash
// to the same element. Every link and CTA is a real anchor - no role="button".
// The only <button>s are in-place actions: burger, language, accessibility.
//
// Motion-off needs no code here beyond the magnet bail: the header carries the
// `hl-root` class, so homepage.css's `html.lf-motion-off .hl-root *` block
// zeroes every transition and animation in homeNav.css (compact, has-dock,
// link fades, the sheet's entrance).
//
// ONE SYSTEM WITH THE APP NAVBAR (brand rollout, navy audit rounds 2-4): the
// pill surface, the brand lockup link (components/brand/Logo.js - never a
// typed wordmark), the text nav item with its navy active pill, the button
// skins and the burger are the shared `lf-pill-*` classes of
// components/navbar/pillNav.css, which Navbar.js renders too. The `hl-nav-*`
// classes stay beside them as this pill's own handles (tests, the inert
// sweep, the phone CTA reveal, the dock).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import FocusTrap from "@mui/material/Unstable_TrapFocus";
import {
  BookOpen,
  CircleHelp,
  Globe,
  Menu as MenuIcon,
  PersonStanding,
  Star,
  Tag,
  Target,
  Users,
  Workflow,
  X as CloseIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
// Routes only - the registry is plain CommonJS data and pulls in no profession
// content, which is what keeps every profession's copy out of the eager bundle
// this component rides in.
import professionRegistry from "../../content/professions/registry";
import useMotion from "../../motion/useMotion";
import SimpleAccessibilitySettings from "../SimpleAccessibilitySettings";
import Logo from "../brand/Logo";
import { NAV_LOCKUP_HEIGHT, NAV_MENU_BREAKPOINT_PX } from "../navbar/navTokens";
import { DOCK_SLOT_ID } from "./ScannedTray";
// The shared pill vocabulary FIRST, then this pill's own rules on top of it
// (same-specificity rules resolve by order).
import "../navbar/pillNav.css";
import "../../homepage/homeNav.css";

/** The prototype's `scrollY>80`. */
export const COMPACT_SCROLL_Y = 80;
/** The prototype's scroll-spy band. */
export const SPY_ROOT_MARGIN = "-40% 0px -55% 0px";
/** The prototype's `.nav-links{display:none}` breakpoint - the burger's too. */
export const MENU_BREAKPOINT_PX = NAV_MENU_BREAKPOINT_PX;
/** The phone sheet's id: the burger's `aria-controls` target. */
export const SHEET_ID = "hl-nav-sheet";
/** The section whose arrival in the spy band CLEARS the active link. */
export const SPY_CLEAR_ID = "hero";
/** AuthLayout's <main>: made inert behind the open sheet. */
export const MAIN_CONTENT_ID = "main-content";
/**
 * Put on <html> while the phone sheet is open. The ONLY consumer is
 * homepage.css's `.hl-tray-chip` rule: the docked counter chip is portalled to
 * <body>, so no selector rooted at this header can reach it, and it would
 * otherwise paint over the sheet's scrim. Nothing reads it in JS.
 */
export const MENU_LOCK_CLASS = "hl-nav-menu-lock";
/** The hero's own primary CTA - the sentinel the phone nav CTA waits for. */
export const HERO_CTA_SELECTOR = "[data-hero-cta]";
/**
 * The homepage is a lazy route, so its hero can mount AFTER this header does.
 * Look for the sentinel this many times, this far apart, before giving up and
 * showing the CTA (the pre-round-3 behaviour - fail open, never fail blank).
 */
export const HERO_CTA_LOOKUP_MS = 250;
export const HERO_CTA_LOOKUPS = 20;

/**
 * The sheet's row icons - the same lucide set the production Navbar uses for
 * the same seven links, so the two menus read as one product.
 */
export const SHEET_ICONS = {
  clients: Users,
  "how-it-works": Workflow,
  features: Star,
  "opportunity-types": Target,
  pricing: Tag,
  faq: CircleHelp,
  blog: BookOpen,
};
/** The sheet icons' size and stroke (owner round 3). */
export const SHEET_ICON_SIZE = 18;
export const SHEET_ICON_STROKE = 1.75;
/**
 * The brand lockup's rendered height on a wide screen. The `<img>`'s own
 * width/height attributes come from it (no layout shift); pillNav.css drops it
 * to 22px on a phone and to 20px at <= 340px (owner 2026-10-14: 22px, with the
 * 340px step only so the docked row fits a 320px phone).
 */
export const BRAND_LOCKUP_HEIGHT = NAV_LOCKUP_HEIGHT;

/** A section link's href: an in-page fragment on "/", a route + hash elsewhere. */
export function sectionHref(onHome, id) {
  return onHome ? `#${id}` : `/#${id}`;
}

/**
 * The seven spied sections, in the prototype's order. Ids are the SectionShell
 * ids in components/homepage/*Live.js; the English labels are the production
 * Navbar's for the same links (the prototype is Hebrew-only).
 */
export const NAV_LINKS = [
  { id: "clients", he: "לקוחות", en: "Clients" },
  { id: "how-it-works", he: "איך זה עובד", en: "How it works" },
  { id: "features", he: "יתרונות", en: "Benefits" },
  { id: "opportunity-types", he: "סוגי הזדמנויות", en: "Opportunity Types" },
  { id: "pricing", he: "מחירים", en: "Pricing" },
  { id: "faq", he: "שאלות נפוצות", en: "FAQ" },
  { id: "blog", he: "בלוג", en: "Blog" },
];

/**
 * A profession landing page's links: THE HOMEPAGE'S OWN SEVEN, unchanged.
 *
 * Since owner decision 2026-09-22 the page mounts FeaturesLive and TypesLive
 * too, so every id the homepage pill points at is a section this page renders
 * as well - and in the same relative order (the profession's own
 * `#opportunity` section sits between features and types, but it is not a
 * pill). There is therefore nothing left to subset or reorder, and this is a
 * named ALIAS rather than a second list: a hand-kept copy could only ever
 * drift, and the one way it drifted mattered - `pricing` before
 * `opportunity-types` while the page renders them the other way round - made
 * the scroll-spy highlight jump backwards as the visitor scrolled forwards.
 *
 * The blog entry is an IN-PAGE ANCHOR, like every other pill: the page ends on
 * the same BlogLive strip the homepage does, pinned to this profession's three
 * articles, and `blog` is in PROFESSION_SECTION_IDS. It used to be a `to:
 * "/blog"` route link, on a comment claiming the page carried no strip to
 * anchor to - stale since the strip shipped, and it sent a reader off the page
 * instead of 200px down it.
 *
 * The name stays because the NAV_PRESETS keys are what drive `ownSections`,
 * and because a future profession page that really does drop a section has an
 * obvious place to become its own list again.
 */
export const PROFESSION_NAV_LINKS = NAV_LINKS;

/**
 * Which pages render their OWN anchored sections, and which links to show.
 *
 * A pathname listed here gets in-page fragment links, the scroll-spy and no
 * top spacer - the homepage's behaviour, because these pages are the homepage
 * with different words. Any other route keeps what it has always had: the full
 * seven as `/#id` RouterLinks back to the homepage, spy off, spacer on.
 *
 * DERIVED from the profession registry, never listed by hand: a new profession
 * page is one line in `content/professions/registry.js`, and forgetting it
 * here used to mean the page silently served the homepage's preset (pill links
 * pointing at sections it does not have, plus a spacer shoving its 100vh hero
 * down the screen).
 */
export const NAV_PRESETS = {
  "/": NAV_LINKS,
  ...Object.fromEntries(professionRegistry.professionRoutes().map((route) => [route, PROFESSION_NAV_LINKS])),
};

// Built ONCE at module load, not per render: the registry is frozen for the
// life of the process, and `professionRoutes()` allocates a fresh array on
// every call.
const PROFESSION_ROUTE_SET = new Set(professionRegistry.professionRoutes());

/**
 * A pathname reduced to the form the maps above are keyed in: trailing slashes
 * stripped, "/" left alone.
 *
 * NOT cosmetic. `buildCanonicalUrl` emits the TRAILING-SLASH form, the sitemap
 * lists `https://leadifyai.biz/lawyers/`, and the prerender writes
 * `build/lawyers/index.html` - which the bucket serves at 200 for `/lawyers/`;
 * `/lawyers` 301s to `/lawyers/index.html`, which the app rewrites to
 * `/lawyers/` before React mounts (utils/indexHtmlUrl.js). So the canonical
 * URL, the sitemap URL and the URL
 * every search result sends a visitor to is the one with the slash, and an
 * exact-match lookup missed all of them: that visitor got the homepage preset
 * (pills pointing off-page, plus the 92px spacer under a 100vh hero) and, if
 * signed in, the dashboard chip the owner forbade on these pages. Every lookup
 * in this file goes through here.
 */
export function normalizePathname(pathname) {
  const stripped = String(pathname || "/").replace(/\/+$/, "");
  return stripped || "/";
}

/** Is this pathname one of the profession landing pages? */
export function isProfessionRoute(pathname) {
  return PROFESSION_ROUTE_SET.has(normalizePathname(pathname));
}

/** The links for a pathname. Anything unlisted gets the homepage's set. */
export function navLinksFor(pathname) {
  return NAV_PRESETS[normalizePathname(pathname)] || NAV_LINKS;
}

/** Is the slot currently holding the docked counter? (ScannedTray sizes it.) */
function slotIsOccupied(slot) {
  if (!slot) return false;
  const width = parseFloat(slot.style.width);
  return Number.isFinite(width) && width > 0;
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew] AuthLayout's language; Hebrew is the
 *   prototype, English mirrors the production Navbar's labels.
 * @param {() => void} [props.toggleLanguage] AuthLayout's switch - the same
 *   function the production Navbar receives.
 */
export default function HomeNav({ isHebrew = true, toggleLanguage }) {
  const { user } = useAuth();
  const { off: motionOff } = useMotion();
  const { pathname: rawPathname } = useLocation();
  // Trailing-slash normalised ONCE, here: `/lawyers/` is the canonical, the
  // sitemap entry and what the bucket serves at 200, so it is the spelling
  // most real visitors arrive on (see normalizePathname).
  const pathname = normalizePathname(rawPathname);
  // Does THIS page render the sections the pill points at? True on "/" and on
  // every profession landing page. It REPLACES the old `pathname === "/"` test
  // on the fragment links, the spy, the hero-CTA sentinel and the spacer - on
  // "/" it is that same value, which is what keeps the homepage unchanged, and
  // on a profession page it is what stops the pill linking to sections the
  // page does not have and pushing a 100vh hero 92px down the screen.
  const ownSections = Boolean(NAV_PRESETS[pathname]);
  const links = navLinksFor(pathname);
  // A PROFESSION PAGE ALWAYS SHOWS THE SIGNED-OUT PAIR (owner, 2026-09-22):
  // "הצטרף" + "התחבר", never the dashboard chip, whoever is reading it. That
  // IS the homepage's behaviour - RootRoute.js sends a signed-in visitor
  // straight to /dashboard, so "/" is only ever rendered for a signed-out
  // reader and its pill only ever shows those two. A profession landing is a
  // paid-acquisition destination reached from search and from ads: it has to
  // read as an acquisition page, and a stale session on a shared or second
  // device must not turn its only call to action into "go to your dashboard".
  const showAuthedCta = Boolean(user) && !isProfessionRoute(pathname);
  const [compact, setCompact] = useState(false);
  const [hasDock, setHasDock] = useState(false);
  const [active, setActive] = useState("");
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Has the hero's own register button scrolled above the viewport? Only then
  // does the phone pill grow its CTA. False = collapsed, which is what a
  // visitor who has not scrolled yet sees.
  const [heroCtaPassed, setHeroCtaPassed] = useState(false);
  const slotRef = useRef(null);
  const magnetRef = useRef(null);
  const burgerRef = useRef(null);
  const innerRef = useRef(null);
  // Whether the NEXT close should hand focus back to the burger. True for
  // every close the visitor made; the media-query close clears it first.
  const restoreFocusRef = useRef(true);
  const dir = isHebrew ? "rtl" : "ltr";

  const text = useMemo(
    () => ({
      signUp: isHebrew ? "הצטרף" : "Sign Up",
      login: isHebrew ? "התחבר" : "Login",
      dashboard: isHebrew ? "לדשבורד" : "Dashboard",
      navLabel: isHebrew ? "ניווט ראשי" : "Main navigation",
      // The language control names the ACTION, not just the target language
      // (final review #17 - wording pending owner approval); the sheet still
      // shows the target language as its visible text, which the accessible
      // name contains.
      language: isHebrew ? "Switch to English" : "עבור לעברית",
      languageVisible: isHebrew ? "English" : "עברית",
      languageShort: isHebrew ? "EN" : "עב",
      accessibility: isHebrew ? "נגישות" : "Accessibility",
      menuOpen: isHebrew ? "פתח תפריט" : "Open menu",
      menuClose: isHebrew ? "סגור תפריט" : "Close menu",
      menu: isHebrew ? "תפריט" : "Menu",
    }),
    [isHebrew]
  );

  // ---------------------------------------------------------------- compact
  useEffect(() => {
    let last = null;
    const onScroll = () => {
      const next = window.scrollY > COMPACT_SCROLL_Y;
      if (next === last) return;
      last = next;
      setCompact(next);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // --------------------------------------------------------------- has-dock
  useEffect(() => {
    const slot = slotRef.current;
    if (!slot || typeof MutationObserver === "undefined") return undefined;
    setHasDock(slotIsOccupied(slot));
    const observer = new MutationObserver(() => setHasDock(slotIsOccupied(slot)));
    observer.observe(slot, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);

  // ----------------------------------------------------- the hero CTA gate
  // The phone pill's CTA appears once the hero's own register button has left
  // the top of the viewport, and disappears again on the way back up. Watched
  // with an IntersectionObserver on the button itself rather than a scroll
  // number, because the button's position is a layout result (401px at 390,
  // 595px at 1440) and a number would be right on exactly one width.
  //
  // `isIntersecting === false` alone is not enough: a target BELOW the fold is
  // also not intersecting, which is the state on a route that renders the hero
  // further down. The sign of `boundingClientRect.top` says which side it left
  // by, and only "above" counts as passed.
  useEffect(() => {
    if (!ownSections || typeof IntersectionObserver === "undefined") {
      setHeroCtaPassed(true);
      return undefined;
    }
    let observer = null;
    let timer = 0;
    let tries = 0;
    const attach = () => {
      timer = 0;
      const el = document.querySelector(HERO_CTA_SELECTOR);
      if (!el) {
        tries += 1;
        if (tries >= HERO_CTA_LOOKUPS) {
          setHeroCtaPassed(true);
          return;
        }
        timer = window.setTimeout(attach, HERO_CTA_LOOKUP_MS);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          const rect = entry.boundingClientRect;
          setHeroCtaPassed(!entry.isIntersecting && !!rect && rect.top < 0);
        },
        { threshold: 0 }
      );
      observer.observe(el);
    };
    attach();
    return () => {
      if (timer) window.clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [ownSections]);

  // ------------------------------------------------------------- scroll-spy
  useEffect(() => {
    if (!ownSections || typeof IntersectionObserver === "undefined") return undefined;
    const targets = links.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!targets.length) return undefined;
    // The hero is not a link, but it is a target: when it re-enters the band
    // the spy has nothing to point at, so it points at nothing.
    const hero = document.getElementById(SPY_CLEAR_ID);
    if (hero) targets.unshift(hero);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id === SPY_CLEAR_ID ? "" : entry.target.id);
        });
      },
      { rootMargin: SPY_ROOT_MARGIN }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ownSections, links]);

  // ----------------------------------------------------------------- magnet
  useEffect(() => {
    const el = magnetRef.current;
    if (!el) return undefined;
    const touch =
      typeof window.matchMedia === "function" && window.matchMedia("(hover: none)").matches;
    if (motionOff || touch) {
      el.style.transform = "";
      return undefined;
    }
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${dx * 0.22}px,${dy * 0.28}px)`;
    };
    const onLeave = () => {
      el.style.transform = "";
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      el.style.transform = "";
    };
  }, [motionOff, user]);

  // Alt + A opens the accessibility settings, as the production Navbar does on
  // every other route.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || e.key !== "a") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      setAccessibilityOpen((prev) => !prev);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  const closeAccessibility = useCallback(() => setAccessibilityOpen(false), []);
  const openAccessibility = useCallback(() => {
    setMenuOpen(false);
    setAccessibilityOpen(true);
  }, []);
  const onToggleLanguage = useCallback(() => {
    setMenuOpen(false);
    if (typeof toggleLanguage === "function") toggleLanguage();
  }, [toggleLanguage]);

  // ------------------------------------------------------------- phone menu
  const closeMenu = useCallback(() => {
    restoreFocusRef.current = true;
    setMenuOpen(false);
  }, []);
  const toggleMenu = useCallback(() => {
    restoreFocusRef.current = true;
    setMenuOpen((prev) => !prev);
  }, []);

  // While the sheet is open: Escape closes it, the page behind does not
  // scroll and is inert (the pill's own content except the burger, and the
  // layout's <main>), and growing past the breakpoint (a rotation, a resize)
  // closes it because the burger itself disappears there. Focus returns to
  // the burger on a close the visitor made, so a keyboard user is not dropped
  // at the top of the document; the media-query close leaves focus alone.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const burger = burgerRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        restoreFocusRef.current = true;
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    // The scanned-posts chip docks INTO this pill but is portalled to <body>
    // (ScannedTray: the hero's stage has `perspective`, so a fixed child of it
    // is positioned against the stage). That puts it outside both the `inert`
    // sweep below and the header's stacking context, so while the sheet is
    // open it would float over the scrim. A marker on <html> is the only
    // handle a portalled node shares with this one; homepage.css hides the
    // chip under it. Not `inert`: the chip is not interactive, and this has to
    // hide it visually.
    document.documentElement.classList.add(MENU_LOCK_CLASS);
    // `inert` as an attribute, not the IDL property: jsdom has no `inert`
    // setter, and the attribute is what the browser reads either way.
    const inertTargets = [];
    const inner = innerRef.current;
    if (inner) {
      Array.from(inner.children).forEach((child) => {
        if (child !== burger) inertTargets.push(child);
      });
    }
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (main) inertTargets.push(main);
    inertTargets.forEach((el) => el.setAttribute("inert", ""));
    let mql = null;
    const onChange = (e) => {
      if (e.matches) return;
      restoreFocusRef.current = false;
      setMenuOpen(false);
    };
    if (typeof window.matchMedia === "function") {
      mql = window.matchMedia(`(max-width: ${MENU_BREAKPOINT_PX}px)`);
      if (typeof mql.addEventListener === "function") mql.addEventListener("change", onChange);
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      document.documentElement.classList.remove(MENU_LOCK_CLASS);
      inertTargets.forEach((el) => el.removeAttribute("inert"));
      if (mql && typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", onChange);
      }
      if (restoreFocusRef.current) burger?.focus({ preventScroll: true });
      restoreFocusRef.current = true;
    };
  }, [menuOpen]);

  // The wordmark: already on "/" it is "go to top" (the prototype's #top),
  // anywhere else a normal link home.
  const onWordmarkClick = useCallback(
    (e) => {
      if (window.location.pathname !== "/") return;
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: motionOff ? "auto" : "smooth" });
    },
    [motionOff]
  );

  const headerClass = [
    "hl-root",
    "lf-pill-scope",
    "hl-nav",
    compact ? "hl-nav-compact" : "",
    hasDock ? "hl-nav-has-dock" : "",
    menuOpen ? "hl-nav-menu-open" : "",
    heroCtaPassed ? "hl-nav-cta-in" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // A section link, rendered twice (the pill's row and the sheet). On "/" a
  // plain fragment anchor - the browser scrolls; elsewhere a RouterLink to
  // "/" carrying the hash, which useScrollToHash follows once the homepage
  // has mounted. `data-spy` marks only the anchors the spy can light.
  // `withIcon` is the sheet's row (owner round 3): a lucide glyph at the row's
  // inline start, then the label. The pill's row stays text-only.
  const renderSectionLink = (l, onClick, withIcon = false) => {
    const label = isHebrew ? l.he : l.en;
    const isActive = active === l.id;
    // The pill's row takes the shared nav item (and its navy active pill);
    // the sheet's rows keep their own, larger shape in homeNav.css.
    const className =
      [withIcon ? "" : "lf-pill-link", isActive ? "hl-nav-active" : "", isActive && !withIcon ? "lf-pill-link-active" : ""]
        .filter(Boolean)
        .join(" ") || undefined;
    const current = isActive ? "location" : undefined;
    const Icon = withIcon ? SHEET_ICONS[l.id] : null;
    const body = Icon ? (
      <>
        <Icon
          size={SHEET_ICON_SIZE}
          strokeWidth={SHEET_ICON_STROKE}
          aria-hidden="true"
          focusable="false"
        />
        <span>{label}</span>
      </>
    ) : (
      label
    );
    // Every entry is a SECTION: either an in-page anchor on a page that
    // renders it, or a `/#id` route link back to the homepage. The per-entry
    // `to` escape hatch that used to sit here (the profession pages' blog
    // pill, before the page grew its own blog strip) is gone with its last
    // user - an unreachable branch is not a design, and re-adding one is four
    // lines on the day a preset needs it.
    if (ownSections) {
      return (
        <a
          key={l.id}
          href={sectionHref(true, l.id)}
          data-spy=""
          className={className}
          aria-current={current}
          onClick={onClick}
        >
          {body}
        </a>
      );
    }
    return (
      <Link key={l.id} to={sectionHref(false, l.id)} className={className} aria-current={current} onClick={onClick}>
        {body}
      </Link>
    );
  };

  // The utilities pair, rendered twice: inline in the pill (desktop) and as a
  // labelled row in the sheet (phone). Both call the same handlers.
  const renderUtilities = (inSheet) => (
    <>
      <button
        type="button"
        className={inSheet ? "hl-nav-sheet-util" : "hl-nav-util hl-nav-util-lang"}
        onClick={onToggleLanguage}
        aria-label={text.language}
        title={inSheet ? undefined : text.language}
        lang={isHebrew ? "en" : "he"}
      >
        <Globe aria-hidden="true" focusable="false" />
        <span className={inSheet ? undefined : "hl-nav-util-txt"} aria-hidden={inSheet ? undefined : "true"}>
          {inSheet ? text.languageVisible : text.languageShort}
        </span>
      </button>
      <button
        type="button"
        className={inSheet ? "hl-nav-sheet-util" : "hl-nav-util hl-nav-util-a11y"}
        onClick={openAccessibility}
        aria-label={text.accessibility}
        title={inSheet ? undefined : text.accessibility}
        aria-haspopup="dialog"
      >
        <PersonStanding aria-hidden="true" focusable="false" />
        {inSheet ? <span>{text.accessibility}</span> : null}
      </button>
    </>
  );

  return (
    <>
      <header className={headerClass} role="banner" data-testid="hl-nav">
        {menuOpen ? (
          <div className="hl-nav-scrim" onClick={closeMenu} aria-hidden="true" data-testid="hl-nav-scrim" />
        ) : null}
        <div className="hl-nav-inner lf-pill-surface" ref={innerRef}>
          {/* The brand lockup (icon + full name, owner rounds 2-3). The IMAGE
              names the link (alt "LeadifyAI") and the link carries no
              aria-label of its own, so its accessible name is exactly
              "LeadifyAI", announced once (the app Navbar does the same). */}
          <Link
            to="/"
            className="hl-nav-wordmark lf-pill-brand"
            onClick={onWordmarkClick}
          >
            <Logo variant="lockup" height={BRAND_LOCKUP_HEIGHT} />
          </Link>
          <span className="hl-nav-dock" id={DOCK_SLOT_ID} ref={slotRef} aria-hidden="true" />
          <nav className="hl-nav-links" aria-label={text.navLabel} dir={dir}>
            {links.map((l) => renderSectionLink(l, undefined))}
          </nav>
          {/* `hl-nav-cta` marks whichever action is the pill's headline one -
              the register button signed out, the dashboard link signed in. It
              is the element the phone pill collapses and grows again. On a
              profession landing page it is always the register button: see
              `showAuthedCta`. */}
          <div className="hl-nav-actions" dir={dir}>
            {showAuthedCta ? (
              <Link to="/dashboard" className="hl-nav-btn hl-nav-btn-ghost lf-pill-btn lf-pill-btn-ghost hl-nav-cta">
                {text.dashboard}
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="hl-nav-btn hl-nav-btn-primary lf-pill-btn lf-pill-btn-primary hl-nav-cta"
                  data-magnet=""
                  ref={magnetRef}
                >
                  {text.signUp}
                </Link>
                <Link to="/login" className="hl-nav-btn hl-nav-btn-ghost lf-pill-btn lf-pill-btn-ghost hl-nav-login">
                  {text.login}
                </Link>
              </>
            )}
            <div className="hl-nav-utils" data-testid="hl-nav-utils">
              {renderUtilities(false)}
            </div>
          </div>
          {/* LAST in the row on purpose: at the phone breakpoint it is the
              pill's right end, and tab order then follows the eye. Above
              1080px it is display:none and the row is the prototype's. */}
          <button
            type="button"
            className="hl-nav-burger lf-pill-burger"
            ref={burgerRef}
            onClick={toggleMenu}
            aria-expanded={menuOpen}
            aria-controls={SHEET_ID}
            aria-label={menuOpen ? text.menuClose : text.menuOpen}
          >
            {/* Lucide's menu / close glyph (owner, 2026-10-14); the button
                carries the name and the state. */}
            {menuOpen ? (
              <CloseIcon size={20} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
            ) : (
              <MenuIcon
                className="lf-pill-burger-lines"
                size={20}
                strokeWidth={2}
                absoluteStrokeWidth
                aria-hidden="true"
              />
            )}
          </button>
        </div>
        {/* The trap keeps focus in the sheet; the restore on close is this
            component's (the effect above), so the trap's own is off. */}
        {menuOpen ? (
          <FocusTrap open disableRestoreFocus>
            <div
              className="hl-nav-sheet"
              id={SHEET_ID}
              role="dialog"
              aria-modal="true"
              aria-label={text.menu}
              dir={dir}
              tabIndex={-1}
              data-testid="hl-nav-sheet"
            >
              <nav className="hl-nav-sheet-links" aria-label={text.menu}>
                {links.map((l) => renderSectionLink(l, closeMenu, true))}
              </nav>
              <div className="hl-nav-sheet-utils">{renderUtilities(true)}</div>
              <div className="hl-nav-sheet-ctas">
                {showAuthedCta ? (
                  <Link to="/dashboard" className="hl-nav-btn hl-nav-btn-ghost lf-pill-btn lf-pill-btn-ghost" onClick={closeMenu}>
                    {text.dashboard}
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="hl-nav-btn hl-nav-btn-primary lf-pill-btn lf-pill-btn-primary" onClick={closeMenu}>
                      {text.signUp}
                    </Link>
                    <Link to="/login" className="hl-nav-btn hl-nav-btn-ghost lf-pill-btn lf-pill-btn-ghost" onClick={closeMenu}>
                      {text.login}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </FocusTrap>
        ) : null}
      </header>
      {ownSections ? null : (
        <div className="hl-nav-spacer lf-pill-scope" aria-hidden="true" data-testid="hl-nav-spacer" />
      )}
      <SimpleAccessibilitySettings open={accessibilityOpen} onClose={closeAccessibility} />
    </>
  );
}
